import React, { useEffect, useMemo, useState } from "react";
import {
  Badge,
  Box,
  Button,
  Flex,
  HStack,
  IconButton,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Select,
  Spinner,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  useBreakpointValue,
  VStack,
} from "@chakra-ui/react";
import { Link, useNavigate } from "react-router-dom";
import { FaEye, FaPencilAlt, FaTrash } from "react-icons/fa";
import { TriangleDownIcon, TriangleUpIcon } from "@chakra-ui/icons";
import axios from "../../axiosConfig";
import Widget from "../widget/Widget";
import ListPageLayout from "../common/ListPageLayout";
import TruncatedText from "../common/TruncatedText";

const TITLE_MAX_CHARS = 80;

const WidgetList = () => {
  const [widgets, setWidgets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const navigate = useNavigate();
  const isMobile = useBreakpointValue({ base: true, md: false });

  const [previewOpen, setPreviewOpen] = useState(false);
  const [activeWidgetId, setActiveWidgetId] = useState(null);

  // ✅ Sorting state
  const [sortKey, setSortKey] = useState("title"); // title | language | steps
  const [sortDir, setSortDir] = useState("asc"); // asc | desc

  const openPreview = (id) => {
    setActiveWidgetId(id);
    setPreviewOpen(true);
  };

  const closePreview = () => {
    setPreviewOpen(false);
    setActiveWidgetId(null);
  };

  useEffect(() => {
    const fetchWidgets = async () => {
      try {
        const response = await axios.get("/widgets");
        setWidgets(response.data);
      } catch (err) {
        console.error("Error fetching widgets:", err);
        setError("Failed to load widgets");
      } finally {
        setLoading(false);
      }
    };
    fetchWidgets();
  }, []);

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this widget?")) return;

    try {
      await axios.delete(`/widgets/${id}`);
      setWidgets((prev) => prev.filter((w) => w.id !== id));
    } catch (err) {
      console.error("Error deleting widget:", err);
      alert("Failed to delete widget.");
    }
  };

  const sortedWidgets = useMemo(() => {
    const dir = sortDir === "asc" ? 1 : -1;
    const normalize = (v) => (v ?? "").toString().trim().toLowerCase();

    const getValue = (w) => {
      switch (sortKey) {
        case "language":
          return normalize(w.language);
        case "steps":
          return Number(w.steps_count ?? 0);
        case "title":
        default:
          return normalize(w.title);
      }
    };

    return [...widgets].sort((a, b) => {
      const av = getValue(a);
      const bv = getValue(b);

      if (typeof av === "number" && typeof bv === "number") {
        if (av !== bv) return (av - bv) * dir;
      } else {
        const cmp = av.localeCompare(bv);
        if (cmp !== 0) return cmp * dir;
      }

      const titleCmp = normalize(a.title).localeCompare(normalize(b.title));
      if (titleCmp !== 0) return titleCmp;
      return (a.id ?? 0) - (b.id ?? 0);
    });
  }, [widgets, sortKey, sortDir]);

  const toggleSort = (key) => {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
  };

  const SortIndicator = ({ active }) => {
    if (!active) return null;
    return sortDir === "asc" ? (
      <TriangleUpIcon ml={2} boxSize="10px" />
    ) : (
      <TriangleDownIcon ml={2} boxSize="10px" />
    );
  };

  const renderWidgetCard = (widget) => (
    <Box
      key={widget.id}
      p={4}
      border="1px solid"
      borderColor="gray.200"
      borderRadius="xl"
      shadow="sm"
      w="100%"
      bg="white"
      mb={4}
      _hover={{ shadow: "md", borderColor: "gray.300" }}
    >
      <VStack align="start" spacing={2} w="100%">
        <TruncatedText
          value={widget.title}
          maxChars={TITLE_MAX_CHARS}
          fontWeight="semibold"
          fontSize="md"
        />

        <HStack flexWrap="wrap" spacing={2}>
          <Badge colorScheme="blue">Lang: {widget.language}</Badge>
          <Badge colorScheme="green">Steps: {widget.steps_count ?? 0}</Badge>
        </HStack>

        <HStack mt={2} spacing={2} flexWrap="wrap">
          <Button
            leftIcon={<FaEye />}
            colorScheme="cyan"
            variant="outline"
            size="sm"
            onClick={() => openPreview(widget.id)}
          >
            View
          </Button>

          <Button
            as={Link}
            to={`/app/widget/${widget.id}/edit`}
            leftIcon={<FaPencilAlt />}
            colorScheme="yellow"
            variant="outline"
            size="sm"
          >
            Edit
          </Button>

          <Button
            leftIcon={<FaTrash />}
            colorScheme="red"
            variant="outline"
            size="sm"
            onClick={() => handleDelete(widget.id)}
          >
            Delete
          </Button>
        </HStack>
      </VStack>
    </Box>
  );

  const renderWidgetTable = () => (
    <Box
      w="100%"
      maxW="100%"
      minW={0}
      border="1px solid"
      borderColor="gray.200"
      borderRadius="xl"
      bg="white"
      shadow="sm"
      overflow="hidden"
    >
      {/* Keep overflow contained here if absolutely necessary */}
      <TableContainer w="100%" maxW="100%" minW={0} overflowX="auto">
        <Table
          variant="unstyled"
          size="md"
          tableLayout="fixed"
          w="100%"
          maxW="100%"
          sx={{
            "th, td": { borderBottom: "1px solid", borderColor: "gray.100" },
            "tbody tr:hover": { background: "gray.50" },
          }}
        >
          <colgroup>
            <col style={{ width: "62%" }} />
            <col style={{ width: "16%" }} />
            <col style={{ width: "10%" }} />
            <col style={{ width: "12%" }} />
          </colgroup>

          <Thead>
            <Tr sx={{ position: "sticky", top: 0, zIndex: 1, background: "white" }}>
              <Th
                cursor="pointer"
                userSelect="none"
                onClick={() => toggleSort("title")}
                py={4}
                fontSize="xs"
                letterSpacing="wider"
                textTransform="uppercase"
              >
                <HStack spacing={1}>
                  <Text>Title</Text>
                  <SortIndicator active={sortKey === "title"} />
                </HStack>
              </Th>

              <Th
                cursor="pointer"
                userSelect="none"
                onClick={() => toggleSort("language")}
                py={4}
                fontSize="xs"
                letterSpacing="wider"
                textTransform="uppercase"
              >
                <HStack spacing={1}>
                  <Text>Language</Text>
                  <SortIndicator active={sortKey === "language"} />
                </HStack>
              </Th>

              <Th
                cursor="pointer"
                userSelect="none"
                onClick={() => toggleSort("steps")}
                isNumeric
                py={4}
                fontSize="xs"
                letterSpacing="wider"
                textTransform="uppercase"
              >
                <HStack spacing={1} justify="flex-end">
                  <Text>Steps</Text>
                  <SortIndicator active={sortKey === "steps"} />
                </HStack>
              </Th>

              <Th py={4} fontSize="xs" letterSpacing="wider" textTransform="uppercase">
                Actions
              </Th>
            </Tr>
          </Thead>

          <Tbody>
            {sortedWidgets.map((widget) => (
              <Tr key={widget.id}>
                <Td py={3}>
                  {/* Critical: allow shrink */}
                  <Box minW={0} w="100%">
                    <TruncatedText
                      value={widget.title}
                      maxChars={TITLE_MAX_CHARS}
                    />
                  </Box>
                </Td>

                <Td py={3}>
                  <Box minW={0} w="100%">
                    <TruncatedText value={widget.language} tooltip={false} />
                  </Box>
                </Td>

                <Td isNumeric py={3} fontVariantNumeric="tabular-nums">
                  {widget.steps_count ?? 0}
                </Td>

                <Td py={3}>
                  <HStack spacing={2}>
                    <Tooltip label="Preview" hasArrow>
                      <IconButton
                        icon={<FaEye />}
                        aria-label="Preview Widget"
                        variant="outline"
                        size="sm"
                        colorScheme="cyan"
                        onClick={() => openPreview(widget.id)}
                      />
                    </Tooltip>

                    <Tooltip label="Edit" hasArrow>
                      <IconButton
                        as={Link}
                        to={`/app/widget/${widget.id}/edit`}
                        icon={<FaPencilAlt />}
                        aria-label="Edit Widget"
                        variant="outline"
                        size="sm"
                        colorScheme="yellow"
                      />
                    </Tooltip>

                    <Tooltip label="Delete" hasArrow>
                      <IconButton
                        icon={<FaTrash />}
                        aria-label="Delete Widget"
                        variant="outline"
                        size="sm"
                        colorScheme="red"
                        onClick={() => handleDelete(widget.id)}
                      />
                    </Tooltip>
                  </HStack>
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </TableContainer>
    </Box>
  );

  const actions = (
    <HStack spacing={3} flexWrap="wrap">
      {isMobile && (
        <Select
          size="sm"
          w="220px"
          value={`${sortKey}:${sortDir}`}
          onChange={(e) => {
            const [k, d] = e.target.value.split(":");
            setSortKey(k);
            setSortDir(d);
          }}
        >
          <option value="title:asc">Title (A–Z)</option>
          <option value="title:desc">Title (Z–A)</option>
          <option value="language:asc">Language (A–Z)</option>
          <option value="language:desc">Language (Z–A)</option>
          <option value="steps:asc">Steps (Low–High)</option>
          <option value="steps:desc">Steps (High–Low)</option>
        </Select>
      )}

      <Button as={Link} to="/app/widget/new" colorScheme="green" variant="outline" size="sm">
        Add Widget
      </Button>
    </HStack>
  );

  return (
    <ListPageLayout title="Widgets" actions={actions}>
      {loading ? (
        <Flex justify="center" mt={10}>
          <Spinner size="xl" color="orange.400" />
        </Flex>
      ) : error ? (
        <Text color="red.500" textAlign="center" mt={10}>
          {error}
        </Text>
      ) : widgets.length > 0 ? (
        isMobile ? (
          <Box mt={2}>{sortedWidgets.map(renderWidgetCard)}</Box>
        ) : (
          <Box mt={2} w="100%" maxW="100%" minW={0}>
            {renderWidgetTable()}
          </Box>
        )
      ) : (
        <Text mt={10} textAlign="center">
          No widgets found.
        </Text>
      )}

      <Modal isOpen={previewOpen} onClose={closePreview} size="6xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Widget Preview</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {activeWidgetId ? <Widget widgetId={activeWidgetId} /> : <Text>No widget selected.</Text>}
          </ModalBody>
        </ModalContent>
      </Modal>
    </ListPageLayout>
  );
};

export default WidgetList;